import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import type { Database } from "@/types/database.types";

type TaskWithDetailsRow = Database["public"]["Views"]["tasks_with_details"]["Row"];

interface KanbanCardProps {
  task: TaskWithDetailsRow;
  onView: (task: TaskWithDetailsRow) => void;
  draggable?: boolean;
}

export function KanbanCard({ task, onView, draggable = false }: KanbanCardProps) {
  const assignee = task.assigned_to && task.assigned_username ? {
    username: task.assigned_username,
    full_name: task.assigned_full_name,
    avatar_url: task.assigned_avatar_url,
  } : null;

  const getInitials = (name: string | null | undefined) => {
    if (!name) return "U";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const formatDueDate = (date: string | null) => {
    if (!date) return null;
    const d = new Date(date);
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };

  return (
    <Card
      className="p-3 cursor-pointer hover:shadow-md transition-shadow bg-background"
      onClick={() => onView(task)}
      draggable={draggable}
      onDragStart={(e) => {
        if (!draggable) return;
        e.dataTransfer.setData("text/task-id", task.id!);
        e.dataTransfer.effectAllowed = "move";
      }}
    >
      <div className="space-y-3">
        <div>
          <h4 className="font-semibold text-sm mb-2">{task.title}</h4>
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {assignee && (
              <Avatar className="h-6 w-6 border border-background">
                <AvatarImage
                  src={assignee.avatar_url || undefined}
                  alt={assignee.full_name || assignee.username || "User"}
                />
                <AvatarFallback className="text-xs">
                  {getInitials(assignee.full_name || assignee.username)}
                </AvatarFallback>
              </Avatar>
            )}
            {task.due_date && (
              <span className="text-xs text-muted-foreground">{formatDueDate(task.due_date)}</span>
            )}
          </div>
        </div>

        {task.tags && task.tags.length > 0 && (
          <div className="flex gap-1 flex-wrap">
            {task.tags.map((tag, idx) => (
              <Badge key={idx} variant="secondary" className="text-xs font-normal">
                {tag}
              </Badge>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
}